import * as React from "react"
import { LabelContext } from "../core/label.context"
import ReactDOM from "react-dom"
import { GeneralContext } from "../core/general.context"
import {BRX_BASE_URL, calls, PROJECT_KEY} from "../core/APIUtils"
import { RESTContext } from "../core/rest.context"
import { ReturnCheckResult } from "../common/common.models"
import { StyledPages } from "./commonPages.style"
import { Button } from "../components/button/button.component"
import { StyledReturn } from "./return.style"
import Select from "react-select";
import { SingleValue} from "react-select"
import { LoaderComponent } from "../components/loader/loader.component"

export const CreateReturnPage = () => {
    const { t } = React.useContext(LabelContext)
    const { userData } = React.useContext(GeneralContext)
    const { ajaxMiddleware$, ajax$, token } = React.useContext(RESTContext)
    const container = document.getElementById("return")

    const [order, setOrder] = React.useState<any>(null)
    const [returnInfo, setReturnInfo] = React.useState<ReturnCheckResult>()
    const [selections, setSelections] = React.useState<any[]>([])
    const [returnCreated, setReturnCreated] = React.useState<boolean>(false)
    const [isLoading, setIsLoading] = React.useState(true)

    const searchParams = new URLSearchParams(window.location.search)
    const orderNumber = searchParams.get("orderNumber")
    const email = searchParams.get("email")
    const orderIdInQueryParam = searchParams.get("orderId")
    const firstNameParam = searchParams.get("firstName")
    const lastNameParam = searchParams.get("lastName")

    React.useEffect(() => {
        if (returnCreated) {
            const cleanUrl = window.location.origin + window.location.pathname
            window.history.replaceState({}, document.title, cleanUrl)
        }
    }, [returnCreated])

    const initReturn = (order: any, returnInfo: ReturnCheckResult) => {
        setOrder(order)
        setReturnInfo(returnInfo)

        const initialSelections = returnInfo.returnableItems.map(ri => ({
            lineItemId: ri.lineItemId,
            selected: false,
            quantity: 1,
            maxQty: ri.quantity,
            comment: ''
        }))

        setSelections(initialSelections)
    }

    React.useEffect(() => {
        if (!container) return

        // guest: ordine passato da form
        if (orderNumber && email) {
            const body = {
                email: email,
                orderNumber: orderNumber,
                projectKey: PROJECT_KEY,
                authToken: token?.access_token
            }
            ajaxMiddleware$(calls.Project.requestReturn(), undefined, body).subscribe({
                next: (res: any) => {
                    if(res.code === 200 && res.obj && res.obj.order && res.obj.returnInfo){
                        initReturn(res.obj.order, res.obj.returnInfo)
                    } else {
                        setOrder(null)
                    }
                    setIsLoading(false)
                },
                error: (err) => {
                    setOrder(null)
                    setIsLoading(false)
                },
            })
            return
        }

        // logged: ordine recuperato da id
        if (orderIdInQueryParam && userData) {
            ajax$(calls.Project.getOrder(orderIdInQueryParam)).subscribe({
                next: order => {
                    const body = {
                        orderNumber: order.orderNumber,
                        projectKey: PROJECT_KEY,
                        authToken: token?.access_token
                    }

                    ajaxMiddleware$(calls.Project.returnStatus(), undefined, body).subscribe({
                        next: (returnInfo: ReturnCheckResult) => {
                            initReturn(order, returnInfo)
                            setIsLoading(false)
                        },
                        error: () => {
                            setOrder(null)
                            setIsLoading(false)
                        }
                    })
                },
                error: () => {
                    setOrder(null)
                    setIsLoading(false)
                }
            })

            return
        }
        setOrder(null)
        setIsLoading(false)
    }, [userData, orderNumber, email])

    const toggleSelect = (lineItemId: string) => {
        setSelections((prev) =>
            prev.map((s) =>
                s.lineItemId === lineItemId ? { ...s, selected: !s.selected } : s
            )
        )
    }

    const updateQuantity = (lineItemId: string, newQty: number) => {
        setSelections((prev) =>
            prev.map((s) =>
                s.lineItemId === lineItemId
                    ? { ...s, quantity: Math.min(Math.max(newQty, 0), s.maxQty) }
                    : s
            )
        )
    }

    const updateComment = (lineItemId: string, newComment: string) => {
        setSelections((prev) =>
            prev.map((s) =>
                s.lineItemId === lineItemId ? { ...s, comment: newComment } : s
            )
        )
    }

    const sanitizeName = (value: string) =>
        value
            .replace(/[\u0000-\u001F\u007F]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 100)

    const joinName = (first?: string | null, last?: string | null) =>
        sanitizeName([first, last].filter(Boolean).join(" "))

    const submitReturn = () => {
        const hasInvalidItem = selections.some(
            (s) => s.selected && s.quantity > 0 && !s.comment
        )

        if (hasInvalidItem) {
            return
        }

        setIsLoading(true)

        const selectedItems = selections
            .filter((s) => s.selected && s.quantity > 0)
            .map((s) => ({
                lineItemId: s.lineItemId,
                quantity: s.quantity,
                comment: s.comment,
            }))

        const isGuest = Boolean(orderNumber && email)
        const name = isGuest
            ? joinName(firstNameParam, lastNameParam)
            : joinName(userData?.firstName, userData?.lastName)

        const body = {
            orderNumber: order.orderNumber,
            projectKey: PROJECT_KEY,
            items: selectedItems,
            authToken: token?.access_token,
            ...(name ? { name } : {})
        }
        ajaxMiddleware$(calls.Project.createReturn(), undefined, body).subscribe({
            next: () => {
                setReturnCreated(true)
                setIsLoading(false)
            },
            error: (err) =>{
                console.log("Error: ", err)
                setIsLoading(false)
            }
        })
    }

    React.useLayoutEffect(() => {
        window.scrollTo({
            top: 0
        });
    }, [isLoading]);

    interface OptionType {
        value: string;
        label: string;
    }

    const options: OptionType[] = [
        {
            value: t('ecommerce.create-return.reason-1'),
            label: t('ecommerce.create-return.reason-1'),
        },
        {
            value: t('ecommerce.create-return.reason-2'),
            label: t('ecommerce.create-return.reason-2'),
        },
        {
            value: t('ecommerce.create-return.reason-3'),
            label: t('ecommerce.create-return.reason-3'),
        },
        {
            value: t('ecommerce.create-return.reason-4'),
            label: t('ecommerce.create-return.reason-4'),
        },
        {
            value: t('ecommerce.create-return.reason-5'),
            label: t('ecommerce.create-return.reason-5'),
        },
        {
            value: t('ecommerce.create-return.reason-6'),
            label: t('ecommerce.create-return.reason-6'),
        },
        {
            value: t('ecommerce.create-return.reason-7'),
            label: t('ecommerce.create-return.reason-7'),
        },
        {
            value: t('ecommerce.create-return.reason-8'),
            label: t('ecommerce.create-return.reason-8'),
        },
    ].filter((o) => o.value?.trim());


    if (!container) return null

    if (!isLoading && container && order === null) {
        return ReactDOM.createPortal(
            <StyledPages>
                <StyledReturn>
                    <div className="container py-5 text-center">
                        <h2>{t("ecommerce.create-return.order-not-found-title")}</h2>
                        <p>{t("ecommerce.create-return.order-not-found-message")}</p>
                    </div>
                </StyledReturn>
            </StyledPages>,
            container
        )
    }

    const customStyles = {
        control: (base: any) => ({
          ...base,
          boxShadow: 'none',
          borderColor: '#ccc',
          '&:hover': {
            borderColor: '#ccc',
          },
        }),
    };

    return ReactDOM.createPortal(
        <>

            {order && returnInfo && !returnCreated && !isLoading && (
                <StyledPages>
                    <StyledReturn>

                        <div className="container return-container">
                            <div className="row align-items-center margin-breadcrumb">
                                <div className="col-12 col-md-6 col-lg-8 order-12 order-md-1">
                                    <h1>{t("ecommerce.create-return.title")}</h1>
                                    <div>{t("ecommerce.create-return.description")}</div>
                                </div>
                            </div>
                        </div>

                        <div className="container">
                        {selections.map((sel, i) => {
                            const li = order.lineItems.find((l: any) => l.id === sel.lineItemId)
                            const img = li?.variant?.images?.[0]?.url

                            return (
                                <div
                                    key={sel.lineItemId}
                                    className="return"
                                >
                                    <div className="row align-items-center">
                                        <div className="col-sm-12 col-lg-2 d-inline-flex">
                                            <div className="checkboxButton align-self-center">
                                                <input
                                                    type="checkbox"
                                                    checked={sel.selected}
                                                    className="checkboxButton"
                                                    id={`checkbox-comment-${i}`}
                                                    name={`checkbox-comment-${i}`}
                                                    onChange={() => toggleSelect(sel.lineItemId)}
                                                />
                                                <label htmlFor={`checkbox-comment-${i}`}></label>
                                            </div>
                                            {img && (
                                                <img
                                                    src={img}
                                                    alt="return-img"
                                                    title="return-img"
                                                />
                                            )}
                                        </div>
                                        <div className="return-info col-sm-12 col-lg-6 pl-5">
                                            <div className="product-name">{li?.name?.[order.locale]}</div>
                                            <div className="sku pb-3">
                                                {li?.productKey}
                                            </div>
                                            <div className="quantity">{`${t("ecommerce.create-return.returnable-quantity")} ${sel.maxQty}`}</div>
                                        </div>

                                        <div className="quantity-section col-sm-12 col-lg-4">
                                            {/* Quantity picker */}
                                            {sel.selected && (
                                                <div className="cartProduct-selectQuantity">
                                                    <span className="label-qnt">{t("ecommerce.myreturns.quantity-label")}</span>
                                                    {(sel.quantity > 1) ?
                                                        <button className="ec_btn ec_btn-decrease" onClick={
                                                            () => {
                                                                updateQuantity(sel.lineItemId, sel.quantity - 1)
                                                            }
                                                        }>
                                                            <span className="icon">-</span>
                                                            <span className="testo">{t("ecommerce.checkout.quantityDecrease")}</span>
                                                        </button>
                                                        :
                                                        <button className="invisible ec_btn ec_btn-decrease"><span className="icon">-</span>
                                                        </button>
                                                    }

                                                    <input className="selected-quantity"
                                                            type=""
                                                            value={sel.quantity}
                                                            onChange={(e) => updateQuantity(sel.lineItemId, parseInt(e.target.value) || 0)}
                                                            readOnly
                                                            name="selected-quantity"
                                                    />

                                                    {(sel.quantity < sel.maxQty) &&
                                                        <button className="ec_btn ec_btn-increase" onClick={
                                                            () => {
                                                                updateQuantity(sel.lineItemId, sel.quantity + 1)
                                                            }
                                                        }>
                                                            <span className="icon">+</span>
                                                            <span className="testo">{t("ecommerce.checkout.quantityIncrease")}</span>
                                                        </button>
                                                    }
                                                    
                                                    <div className="mt-3">
                                                        <Select
                                                            options={options}
                                                            value={options.find((o) => o.value === sel.comment) || null}
                                                            placeholder={t("ecommerce.myreturns.reason-for-return")}
                                                            isSearchable
                                                            onChange={(option: SingleValue<OptionType>) =>
                                                                updateComment(sel.lineItemId, option?.value || '')
                                                            }
                                                            className="text-left"
                                                            styles={customStyles}
                                                         />
                                                    </div>
                                                    
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}

                        <div className="row">
                            <div className="col">
                                <Button
                                    onClick={submitReturn}
                                    btn_disabled={!selections.some(s => s.selected) || selections.some(s => s.selected && (s.quantity <= 0 || !s.comment)) || isLoading}
                                    className="primary float-right"
                                    label={t("ecommerce.create-return.submit-return")}
                                >
                                    {t("ecommerce.create-return.submit-return")}
                                </Button>
                            </div>
                        </div>
                    </div>

                    </StyledReturn>
                </StyledPages>
            )}
            {returnCreated && <ThankYouMessage/>}
        </>,
        container
    )
}

export const ThankYouMessage = (props: any) => {
    const {t} = React.useContext(LabelContext)
    const { userData } = React.useContext(GeneralContext)

    return (
        <StyledPages>
            <StyledReturn>
                <div className="container mb-5">
                    <div className={`row thankyouPage`}>
                        <div className={`col-md-6 col-sm-12`}>
                            <h1>{t("ecommerce.create-return.thankyou-title")}</h1>
                            <p>{t("ecommerce.create-return.thankyou-message1")}</p>
                            <p>{t("ecommerce.create-return.thankyou-message2")}</p>
                            {userData &&
                                <a href={`${BRX_BASE_URL}/my-account#my-returns`} className="mb-5">
                                <b>{t("ecommerce.create-return.goto-my-returns")}</b>
                            </a>}
                        </div>
                    </div>
                </div>
            </StyledReturn>
        </StyledPages>
    )
}
